/**
 * The parent's signed-in session — the credential C1–C5 hold.
 *
 * 🔴 A MODULE, NOT A REACT CONTEXT — the same shape and the same reason as
 * `device.ts`: the token is read by plain `fetch` helpers in `shared/api/`,
 * which are not inside React's lifecycle. A hook-only API would drag the
 * credential into the component tree to no benefit.
 *
 * ✅ THE REFRESH ROUTE IT WAS WAITING FOR NOW EXISTS, AND THE PAIR BELOW IS WHAT
 * USES IT.
 *
 * This file used to read: *"IT EXISTS BECAUSE **THERE IS NO REFRESH ROUTE** …
 * `JWT_ACCESS_TTL` defaults to 15 minutes, so a parent reading C1 for a quarter
 * of an hour is logged out mid-read"*, and it stored the refresh token *"ready
 * for the day the route exists"*. That day arrived: `POST /auth/refresh` is
 * built, and `refresh.ts` rotates through it on every 401 with a single-flight
 * lock. A parent's session now survives a reload, the back button and a closed
 * tab, and ends when they press Sign out — `staffSession.ts` records the same
 * decision for the staff tiers, which used to differ.
 *
 * ⚠️ THE `expired` STATE STAYS, AND IS STILL A SENTENCE RATHER THAN A GATE. A
 * rotation can be refused — a thirty-day refresh token does eventually die, and
 * a signed-out-everywhere parent's dies at once — and *"your session expired,
 * sign in again"* is the honest thing to say then, where a 401 surfacing as
 * *"couldn't load this"* is not.
 */

import { jwtExpiry } from '../lib/jwt'

/**
 * ⚠️ EXPORTED SO THE `storage` EVENT CAN BE RECOGNISED. `AppRouter` listens for
 * another tab writing this key; comparing against a string literal copied into
 * the router is how the two drift the day the key is renamed.
 */
export const PARENT_SESSION_KEY = 'kalqy.parent'

/** Exactly what `POST /auth/parent/otp/verify` returns. */
export interface ParentTokens {
  accessToken: string
  refreshToken: string
}

export type ParentSessionState =
  /** A token that is present and, as far as its own claim says, still valid. */
  | { status: 'ready'; accessToken: string; expiresAt: Date | null }
  /**
   * 🔴 A DISTINCT STATE FROM "signed out", AND THAT IS THE POINT. The parent
   * did sign in and the token simply aged out. Since `refresh.ts` landed the
   * usual repair is a rotation nobody sees, so this state is what is left when
   * that is refused: telling them "sign in again" is honest; telling them the
   * dashboard failed to load is not.
   */
  | { status: 'expired'; expiresAt: Date }
  | { status: 'signed-out' }

/**
 * Minimal `localStorage` shape, injectable so the tests (and Node) do not need
 * a DOM, and so a browser that throws on access cannot take the boot path down.
 * Mirrors `device.ts`'s `DeviceStorage` deliberately — two storage shims with
 * different semantics is how one of them ends up untested.
 */
export interface SessionStorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function defaultStorage(): SessionStorageLike | null {
  try {
    // Absent under Node (where the tests run), and Safari private mode throws
    // on ACCESS rather than on use — so this is a try/catch, not a truthiness
    // check.
    return (globalThis as { localStorage?: SessionStorageLike }).localStorage ?? null
  } catch {
    return null
  }
}

let storage: SessionStorageLike | null = defaultStorage()

/** Test seam. Also the escape hatch if a browser's storage throws. */
export function setParentSessionStorage(next: SessionStorageLike | null): void {
  storage = next
}

function read(): ParentTokens | null {
  let raw: string | null
  try {
    raw = storage?.getItem(PARENT_SESSION_KEY) ?? null
  } catch {
    return null
  }
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { accessToken, refreshToken } = parsed as Partial<ParentTokens>
    /*
     * 🔴 BOTH OR NEITHER, exactly as `device.ts` requires of its registration.
     * A half-cache is a parent the app believes is signed in and cannot ever
     * re-authenticate: every read 401s and the rotation `client.ts` fires has no
     * refresh token to present, so the only escape is clearing site data.
     */
    if (typeof accessToken !== 'string' || accessToken.length === 0) return null
    if (typeof refreshToken !== 'string' || refreshToken.length === 0) return null
    return { accessToken, refreshToken }
  } catch {
    return null
  }
}

/** Called by the OTP screen with the pair `/auth/parent/otp/verify` returned. */
export function setParentSession(tokens: ParentTokens): void {
  try {
    // Written as ONE value: a half-cache is worse than no cache (see above).
    storage?.setItem(PARENT_SESSION_KEY, JSON.stringify(tokens))
  } catch {
    /*
     * Non-fatal. A browser that cannot persist still works for this tab — the
     * token lives in the same string we just failed to write, and the caller
     * holds it. Failing here would turn a storage quirk into a login that
     * cannot complete.
     */
  }
}

/**
 * Sign out.
 *
 * 🔴 THE LOCAL HALF, AND IT IS UNCONDITIONAL — `staffSession.ts`'s rule, for its
 * reason. `POST /auth/logout` revokes the refresh token server-side and the
 * caller fires it afterwards without awaiting it; a sign-out that only works
 * when the wifi does is not one.
 *
 * ⚠️ SINCE THE PAIR PERSISTS, THIS IS THE ONLY THING THAT ENDS A PARENT'S
 * SESSION. Clearing memory and leaving the pair on disk would put the same
 * dashboard back on the next reload.
 */
export function clearParentSession(): void {
  try {
    storage?.removeItem(PARENT_SESSION_KEY)
  } catch {
    // Nothing useful to do; the next read will still parse whatever is there.
  }
}

/**
 * `bearerFor('user')` — the access token, or null.
 *
 * ⚠️ RETURNS THE TOKEN EVEN WHEN IT LOOKS EXPIRED. The `exp` claim below is
 * read **unverified**, so it is a UI hint and never an authorisation decision;
 * the server is the only thing that decides whether a token is good. Withholding
 * a token because our own clock disagrees would log a parent out over a laptop
 * whose date is wrong — the exact class of bug `stampedAt` clamps around on the
 * ingest path.
 */
export function getParentToken(): string | null {
  return read()?.accessToken ?? null
}

/**
 * The refresh token, for `POST /auth/refresh`.
 *
 * ✅ IT HAS A CALLER NOW. This used to say *"nothing calls this yet"* — it was
 * stored against the day the route existed. `refresh.ts` is that caller: one
 * rotation per 401, single-flight, and the pair below replaced on success.
 */
export function getParentRefreshToken(): string | null {
  return read()?.refreshToken ?? null
}

export function getParentSession(now: Date = new Date()): ParentSessionState {
  const tokens = read()
  if (!tokens) return { status: 'signed-out' }

  const expiresAt = accessTokenExpiry(tokens.accessToken)
  if (expiresAt && expiresAt.getTime() <= now.getTime()) return { status: 'expired', expiresAt }
  return { status: 'ready', accessToken: tokens.accessToken, expiresAt }
}

/**
 * The `exp` claim, read from the payload segment.
 *
 * 🔴 UNVERIFIED, AND ONLY EVER USED TO CHOOSE A SENTENCE. The signature is not
 * checked and cannot be — the secret is server-side, which is the whole design.
 * So this must never gate a request or a render decision that matters; it exists
 * so a screen can say *"your session expired"* rather than *"couldn't load
 * this"*. `null` for anything unparseable, so a token shape we do not recognise
 * is treated as "we do not know", never as "expired".
 *
 * ⚠️ THE DECODER MOVED TO `shared/lib/jwt.ts` AND THIS NAME STAYS. §10's staff
 * session needs the identical three lines to read `role`, and two base64url
 * decoders is how one of them ends up untested — the rule `audit-query.ts`
 * records on the backend for the same reason. The behaviour is unchanged;
 * `parentSession.test.ts` is what says so.
 */
export function accessTokenExpiry(token: string): Date | null {
  return jwtExpiry(token)
}
